import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'

interface NewNoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: { title: string; content: string; hashtags: string[] }) => Promise<void>
}

export function NewNoteDialog({ open, onOpenChange, onSubmit }: NewNoteDialogProps) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [hashtagsStr, setHashtagsStr] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('请输入笔记标题')
      return
    }
    if (!content.trim()) {
      toast.error('请输入笔记内容')
      return
    }

    setIsSubmitting(true)
    try {
      const hashtags = hashtagsStr
        .split(/[\s,，#]+/)
        .map(t => t.trim().replace(/^#/, ''))
        .filter(Boolean)
      
      await onSubmit({ title, content, hashtags })
      toast.success('新建笔记成功')
      onOpenChange(false)
      // Reset form
      setTitle('')
      setContent('')
      setHashtagsStr('')
    } catch (error) {
      console.error('新建笔记失败:', error)
      toast.error('新建笔记失败')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>新建笔记</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">标题</Label>
            <Input
              id="title"
              placeholder="输入笔记标题..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="content">正文内容</Label>
            <Textarea
              id="content"
              placeholder="输入笔记正文..."
              className="min-h-[200px]"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="hashtags">话题标签</Label>
            <Input
              id="hashtags"
              placeholder="例如: 探店, 美食, OOTD (空格或逗号分隔)"
              value={hashtagsStr}
              onChange={(e) => setHashtagsStr(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                保存中...
              </>
            ) : (
              '创建笔记'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

